import * as rd from '@devexperts/remote-data-ts'
import { SerializedError, createSelector } from '@reduxjs/toolkit'
import { resetAllState } from '@/store/commonActions.ts'
import { httpClient } from '../lib/http/HttpClient'
import {
  RuleType,
  SectorRuleError,
  SectorRuleResponse,
  UnavailableSectorsResponse,
} from '../models/http'
import { SectorRule } from '../models/insertPeriod'

import { appSlice } from './appSlice'
import { RootState } from './configureStore'
import { createAppSlice } from './createAppSlice'
import { periodAsUnixTimeSelector } from './insertPeriodSlice'
import { licenseSlice } from './licenseSlice'

interface SectorRulesSliceState {
  rules: rd.RemoteData<SerializedError, SectorRule[]>
  preemptionCode: string | null
  unavailableSectors: rd.RemoteData<SerializedError, number[]>
}

const initialState: SectorRulesSliceState = {
  preemptionCode: null,
  rules: rd.initial,
  unavailableSectors: rd.initial,
}

interface UnavailableSectorRuleError {
  ruleType: RuleType
  ruleError: SectorRuleError
}

export const sectorRulesSlice = createAppSlice({
  extraReducers(builder) {
    builder.addCase(resetAllState, () => initialState)
  },
  initialState,
  name: 'sectorRules',
  reducers: (create) => ({
    load: create.asyncThunk<
      SectorRuleResponse,
      string,
      { rejectValue: string }
    >(
      async (license, thunkApi): Promise<SectorRuleResponse> => {
        const rootState = thunkApi.getState() as RootState
        const country = appSlice.selectors.country(rootState)
        const channel = appSlice.selectors.channel(rootState)

        const searchParams = new URLSearchParams({
          channel,
        })

        const res = await httpClient.fetch<SectorRuleResponse>(
          `beaches/${license}/sector-rules?${searchParams}`,
          country
        )

        if (res.status === 'error') {
          throw thunkApi.rejectWithValue(res.error)
        }

        return res.data
      },
      {
        fulfilled: (state, action) => {
          return {
            ...state,
            rules: rd.success(
              (action.payload.result?.sectorRules ?? []).map((item) => ({
                requiresParameter: item.ruleParameter !== null,
                ruleParameter: item.ruleParameter,
                ruleType: item.ruleType,
                validationField: item.validationField,
              }))
            ),
          }
        },
        pending: (state) => {
          return {
            ...state,
            rules: rd.pending,
          }
        },
        rejected: (state, action) => {
          return {
            ...state,
            rules: rd.failure(action.error),
          }
        },
      }
    ),
    loadUnavailableSectors: create.asyncThunk<
      {
        errors: UnavailableSectorRuleError[]
        unavailableSectors: number[]
      },
      Record<string, string>,
      { rejectValue: string }
    >(
      async (
        payload,
        thunkApi
      ): Promise<{
        errors: UnavailableSectorRuleError[]
        unavailableSectors: number[]
      }> => {
        const state = thunkApi.getState() as RootState
        const license = licenseSlice.selectors.license(state)
        const country = appSlice.selectors.country(state)
        const period = periodAsUnixTimeSelector(state)

        if (license === null) {
          const errorMessage = 'License missing'
          throw thunkApi.rejectWithValue(errorMessage)
        }

        const res = await httpClient.fetch<UnavailableSectorsResponse>(
          `beaches/${license.license}/sector-rules/get-unavailable-sectors-new`,
          country,
          {
            body: JSON.stringify({
              ...payload,
              ...(period
                ? {
                    reservationEndDate: period.end,
                    reservationStartDate: period.start,
                  }
                : {}),
            }),
            headers: {
              'Content-Type': 'application/json',
            },
            method: 'POST',
          }
        )

        if (res.status === 'error') {
          throw thunkApi.rejectWithValue(
            res.error.internal_error_code ?? 'Unknown Error'
          )
        }

        const unavailableSectors = res.data.result.unavailableSectors

        const preemptionRuleType: RuleType = 'preemption-code-validation'
        const maxReservationsRuleType: RuleType = 'max-reservations-per-code'

        const errors: UnavailableSectorRuleError[] = unavailableSectors.reduce<
          UnavailableSectorRuleError[]
        >((acc, sector) => {
          if (
            sector.errors.includes(
              `${preemptionRuleType}.${SectorRuleError.INVALID_CODE}`
            )
          ) {
            acc.push({
              ruleError: SectorRuleError.INVALID_CODE,
              ruleType: preemptionRuleType,
            })
          }

          if (
            sector.errors.includes(
              `${maxReservationsRuleType}.${SectorRuleError.TOO_MANY_RESERVATIONS}`
            )
          ) {
            acc.push({
              ruleError: SectorRuleError.TOO_MANY_RESERVATIONS,
              ruleType: maxReservationsRuleType,
            })
          }

          return acc
        }, [])

        return {
          errors,
          unavailableSectors: unavailableSectors.map(
            (sector) => sector.sectorId
          ),
        }
      },
      {
        fulfilled: (state, action) => {
          state.unavailableSectors = rd.success(
            action.payload.unavailableSectors
          )
        },
        options: {
          condition: (_, thunkApi) => {
            const state = thunkApi.getState() as RootState
            const license = licenseSlice.selectors.license(state)
            const unavailableSectors =
              sectorRulesSlice.selectors.unavailableSectors(state)

            if (license === null || rd.isPending(unavailableSectors)) {
              return false
            }

            return true
          },
        },
        pending: (state) => {
          state.unavailableSectors = rd.pending
        },
        rejected: (state, action) => {
          state.unavailableSectors = rd.failure(action.error ?? 'Unknown error')
        },
      }
    ),
    removePreemptionCode: create.reducer((state) => {
      state.preemptionCode = null
      state.unavailableSectors = rd.initial
    }),
    resetUnavailableSectors: create.reducer((state) => {
      state.unavailableSectors = rd.initial
    }),
    setPreemptionCode: create.reducer<string | null>((state, action) => {
      state.preemptionCode = action.payload
    }),
  }),
  selectors: {
    preemptionCode: (state) => state.preemptionCode,
    rules: (state) => (rd.isSuccess(state.rules) ? state.rules.value : null),
    unavailableSectors: (state) => state.unavailableSectors,
  },
})

export const selectHasRuleType = (ruleType: RuleType) =>
  createSelector([sectorRulesSlice.selectors.rules], (rules): boolean => {
    if (rules === null) {
      return false
    }
    return Boolean(rules.find((rule) => rule.ruleType === ruleType))
  })
